/**
 * CardContextMenu (FLOW-18)
 *
 * Right-click / "⋯" menu for a Kanban card offering "Send to top" and "Send
 * to bottom". Portalled because the board column has `overflow-y-auto` and
 * would clip an in-flow menu.
 */

import {
  Component,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { Portal } from "solid-js/web";
import { DropdownItem, focusFirstMenuItem, moveMenuFocus } from "../ui";

const MARGIN = 8;

export interface CardContextMenuProps {
  /** Viewport coordinates the menu should appear at. */
  x: number;
  y: number;
  /** Disable "Send to top" (item is already first in its column). */
  disableTop?: boolean;
  /** Disable "Send to bottom" (item is already last in its column). */
  disableBottom?: boolean;
  onSendToTop: () => void;
  onSendToBottom: () => void;
  onClose: () => void;
}

export const CardContextMenu: Component<CardContextMenuProps> = (props) => {
  const [position, setPosition] = createSignal({ x: props.x, y: props.y });
  let menuRef: HTMLDivElement | undefined;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
      return;
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      moveMenuFocus(menuRef, e.key === "ArrowDown" ? 1 : -1);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (menuRef && !menuRef.contains(e.target as Node)) {
      props.onClose();
    }
  };

  const handleReposition = () => {
    props.onClose();
  };

  createEffect(() => {
    const x = props.x;
    const y = props.y;

    if (menuRef) {
      const rect = menuRef.getBoundingClientRect();
      let clampedX = x;
      let clampedY = y;

      if (clampedX + rect.width > window.innerWidth) {
        clampedX = window.innerWidth - rect.width - MARGIN;
      }
      if (clampedY + rect.height > window.innerHeight) {
        clampedY = window.innerHeight - rect.height - MARGIN;
      }

      clampedX = Math.max(MARGIN, clampedX);
      clampedY = Math.max(MARGIN, clampedY);

      setPosition({ x: clampedX, y: clampedY });
    }
  });

  onMount(() => {
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, { capture: true });

    focusFirstMenuItem(menuRef);
  });

  onCleanup(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("resize", handleReposition);
    window.removeEventListener("scroll", handleReposition, { capture: true });
  });

  const sendToTop = () => {
    props.onSendToTop();
    props.onClose();
  };

  const sendToBottom = () => {
    props.onSendToBottom();
    props.onClose();
  };

  return (
    <Portal>
      <div
        ref={menuRef}
        role="menu"
        aria-label="Card actions"
        tabIndex={-1}
        class="fixed z-50 min-w-[160px] rounded border border-line bg-surface py-1 shadow-overlay outline-none"
        style={{ left: `${position().x}px`, top: `${position().y}px` }}
      >
        <DropdownItem
          onClick={sendToTop}
          disabled={props.disableTop}
          title={props.disableTop ? "Already at the top" : undefined}
        >
          Send to top
        </DropdownItem>
        <DropdownItem
          onClick={sendToBottom}
          disabled={props.disableBottom}
          title={props.disableBottom ? "Already at the bottom" : undefined}
        >
          Send to bottom
        </DropdownItem>
      </div>
    </Portal>
  );
};
